import { RouteLink } from '@mr-yum/frontend-core/dist/services/routes'
import { isServer } from '@mr-yum/frontend-core/dist/support/env'
import { Button, toaster } from '@mr-yum/frontend-ui'
import { CartMemberLoginCTA } from 'components/Cart/CartMemberLoginCTA'
import {
  useOrderingTypeContext,
  useVenueContext,
} from 'contexts/VenueOrderContext'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { isMembershipDisabled } from 'hooks/useIntegrationCheck'
import { config } from 'lib/config'
import {
  useMslAuthSettingsQuery,
  useSwiftPosMemberLoggedInCheckQuery,
} from 'lib/gql'
import { routes } from 'lib/routes'
import React, { useEffect, useState } from 'react'
import { useAmplitude } from 'react-amplitude-hooks'
import { FormattedMessage } from 'react-intl'

interface Props {
  isCart?: boolean
  className?: string
  posIntegrationType?: string | null
}

export const MemberLoginCTA = ({
  isCart = false,
  className,
  posIntegrationType,
}: Props) => {
  const { venueSlug } = useVenueContext()
  const { orderingTypeSlug } = useOrderingTypeContext()
  const { logEvent } = useAmplitude()
  const { currentUser, fetchingCurrentUser } = useCurrentUser()
  const loginRoute =
    config.environment === 'production' ? routes.login : routes.loginSsr

  const membershipIsDisabled = isMembershipDisabled(posIntegrationType)

  const [{ data: settings, fetching: fetchingSettings }] =
    useMslAuthSettingsQuery({
      variables: {
        venueSlug,
      },
      pause: isServer || membershipIsDisabled,
    })

  const [{ data: isMember, fetching: isMemberFetching }] =
    useSwiftPosMemberLoggedInCheckQuery({
      variables: {
        venueSlug,
      },
      pause: isServer || membershipIsDisabled,
      requestPolicy: 'network-only',
    })

  const [showMemberLogin, setShowMemberLogin] = useState<boolean>(false)

  useEffect(() => {
    const query = new URLSearchParams(window.location.search)
    const error = query.get('error')
    if (error) {
      toaster.negative(
        <>
          <FormattedMessage
            defaultMessage="Error authenticating member"
            id="YzBgt9"
          />
        </>,
      )
    }
    const memberLoginCta =
      !isMemberFetching &&
      !isMember?.swiftPosMemberLoggedInCheckV2 &&
      !fetchingCurrentUser &&
      currentUser != null

    setShowMemberLogin(memberLoginCta)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMemberFetching, isMember, fetchingCurrentUser, currentUser])

  if (
    isMemberFetching ||
    isMember?.swiftPosMemberLoggedInCheckV2 ||
    fetchingSettings ||
    !settings?.getMSLAuthSettings?.membershipAuthUrl ||
    !routes.membershipValidation.toPath
  ) {
    return null
  }

  const { search, protocol, hostname, href } = window.location
  let returnTo = new URL(
    `${protocol}//${hostname}${routes.venue.toPath!({
      venueSlug,
    })}`,
  )

  if (isCart && orderingTypeSlug) {
    returnTo = new URL(
      `${protocol}//${hostname}${routes.cart.toPath!({
        venueSlug,
        orderingTypeSlug,
      })}`,
    )
  }

  const membershipUrl = new URL(settings.getMSLAuthSettings.membershipAuthUrl)

  membershipUrl.searchParams.append(
    'returnUrl',
    isServer
      ? ''
      : `${protocol}//${hostname}/${venueSlug}/member-authenticate?redirect=${encodeURIComponent(
          href.replace(search, ''),
        )}&venueSlug=${venueSlug}`,
  )

  return (
    <div className={className}>
      {showMemberLogin && membershipUrl && (
        <Button
          variant="outline"
          as="a"
          onClick={() => window.location.assign(membershipUrl.href)}
          fullWidth
        >
          Member/Loyalty login
        </Button>
      )}

      {!currentUser && (
        <RouteLink
          route={loginRoute}
          pathData={{
            venueSlug,
            returnTo: returnTo.href,
          }}
        >
          {isCart ? (
            <CartMemberLoginCTA
              onClick={() => logEvent('Clicked on member login on cart')}
            />
          ) : (
            <Button
              variant="outline"
              as="a"
              onClick={() => logEvent('Clicked on member login')}
              fullWidth
            >
              <FormattedMessage defaultMessage="Login" id="AyGauy" />
            </Button>
          )}
        </RouteLink>
      )}
    </div>
  )
}
